import store from '@/store';
import products from '@/components/products';
import offline from '@/offline';
import user from '@/components/user';
import api, { craftError, csrfRequest, jsonToMultiPartFormData } from '@/api';
import b62s from '@/offline/base62shrink';
import log from '@/configured/logger';
import { v4 as uuidv4 } from 'uuid';

const exports = {
  namespace: 'outbox',
  module: {
    namespaced: true,
    state: {
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      assets: {},
      lastSentUrl: '',
    },
    mutations: {
      SET_CONTACT_NAME: (state, name) => (state.contactName = name),
      SET_CONTACT_EMAIL: (state, email) => (state.contactEmail = email),
      SET_CONTACT_PHONE: (state, phone) => (state.contactPhone = phone),
      SET_ASSETS: (state, assets) => (state.assets = assets),
      SET_LAST_SENT_URL: (state, url) => (state.lastSentUrl = url),
      CLEAR,
    },
    actions: {
      setContactName: ({ commit }, name) => commit('SET_CONTACT_NAME', name),
      setContactEmail: ({ commit }, email) =>
        commit('SET_CONTACT_EMAIL', email),
      setContactPhone: ({ commit }, phone) =>
        commit('SET_CONTACT_PHONE', phone),
      setAssets: ({ commit }, assets) => commit('SET_ASSETS', assets),
      clear: ({ commit }) => commit('CLEAR'),
      toggleAssetSelection,
      toggleAllAssets,
      sendPackage,
      attachAllForProduct,
      attachItemForProduct,
      removeProduct,
    },
    getters: {
      formattedAssets,
      offlineUrlRaw,
      offlineUrl,
      flatAssets,
      assetIsAttached,
      hasAssets,
      assetCount,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

//////////

function CLEAR(state) {
  state.contactName = '';
  state.contactEmail = '';
  state.contactPhone = '';
  state.assets = {};
}

function attachItemForProduct(
  { commit, state, getters },
  { productId, assetId }
) {
  if (getters.assetIsAttached(productId, assetId)) {
    return;
  }

  let newSelection = JSON.parse(JSON.stringify(state.assets));

  if (!newSelection[productId]) {
    newSelection[productId] = [assetId];
  } else {
    newSelection[productId].push(assetId);
  }

  commit('SET_ASSETS', newSelection);
}

function assetIsAttached(state) {
  return (productId, assetId) => {
    if (!state.assets[productId]) {
      return false;
    }

    return state.assets[productId].filter((e) => e === assetId).length > 0;
  };
}

function attachAllForProduct({ commit, state }, product) {
  let newSelection = JSON.parse(JSON.stringify(state.assets));
  newSelection[product.id] = [];

  if (!product.mediaCollections) {
    return;
  }

  product.mediaCollections.forEach((collection) => {
    let media = collection.media || [];
    media.forEach((asset) => {
      newSelection[product.id].push(asset.id);
    });
  });

  commit('SET_ASSETS', newSelection);
}

function formattedAssets(state, getters) {
  let result = [];

  Object.keys(state.assets).forEach((key, index) => {
    let foundFamily = store.getters[
      `${products.store.namespace}/getProductById`
    ](parseInt(key));
    let familyName =
      (foundFamily && foundFamily.title) || 'Unknown product family';
    let rootCategory =
      ((foundFamily && foundFamily.newProductCategory.length > 0) &&
        store.getters[
          `${products.store.namespace}/getRootParentTitleCategoryById`
        ](foundFamily.newProductCategory[0].id)) ||
      null;
    let rootCategoryDisplay = rootCategory ? rootCategory.title : null;
    let familyDisplay = rootCategoryDisplay
      ? rootCategoryDisplay + ' ' + familyName
      : familyName;

    if (foundFamily && foundFamily.mediaCollections) {
      foundFamily = JSON.parse(JSON.stringify(foundFamily));

      let formattedGroup = {
        id: parseInt(key),
        familyDisplay: familyDisplay,
        allSelected: null, // Updated below
        someSelected: null, // Updated below
        selected: state.assets[key].slice(),
        totalAssetsCount:
          (foundFamily && getAttachableAssetCount(foundFamily)) || 0,
      };

      formattedGroup.allSelected =
        formattedGroup.totalAssetsCount === formattedGroup.selected.length;
      formattedGroup.someSelected =
        formattedGroup.selected.length > 0 &&
        formattedGroup.selected.length < formattedGroup.totalAssetsCount;

      formattedGroup.allAttachableAssets = foundFamily.mediaCollections.filter(
        (collection) => {
          return collection.media && collection.media.length;
        }
      );

      formattedGroup.allAttachableAssets.forEach((collection) => {
        collection.media.forEach((asset) => {
          asset.isSelected = state.assets[foundFamily.id].includes(asset.id);
        });
      });

      result.push(formattedGroup);
    }
  });

  return result;
}

function getAttachableAssetCount(product) {
  let count = 0;

  if (!product.mediaCollections) {
    return count;
  }

  product.mediaCollections.forEach((collection) => {
    if (collection.media) {
      count += collection.media.length;
    }
  });

  return count;
}

function getAllAttachableAssets(product) {
  let assets = [];

  if (!product.allAttachableAssets) {
    return assets;
  }

  product.allAttachableAssets.forEach((collection) => {
    if (collection.media) {
      collection.media.forEach((asset) => {
        assets.push(asset);
      });
    }
  });

  return assets;
}

function toggleAllAssets({ commit, state }, [family]) {
  const allAttachableAssets = getAllAttachableAssets(family);
  const assets = JSON.parse(JSON.stringify(state.assets));

  if (assets[family.id].length !== allAttachableAssets.length) {
    assets[family.id] = [];

    allAttachableAssets.forEach((asset) => {
      assets[family.id].push(asset.id);
    });
  } else {
    assets[family.id] = [];
  }

  commit('SET_ASSETS', assets);
}

function toggleAssetSelection({ commit, state }, [familyId, assetId]) {
  const assets = JSON.parse(JSON.stringify(state.assets));
  let family = assets[familyId];
  let assetIndex = family ? family.indexOf(assetId) : -1;

  if (family && assetIndex >= 0) {
    family.splice(assetIndex, 1);
  } else if (family) {
    family.push(assetId);
  } else {
    assets[familyId] = [assetId];
  }

  commit('SET_ASSETS', assets);
}

function flatAssets(state) {
  let result = [];
  let assets = state.assets;

  Object.keys(assets).forEach((key, index) => {
    result = result.concat(state.assets[key]);
  });

  return result;
}

function offlineUrlRaw(state, getters) {
  let urlData =
    'u=' +
    uuidv4() +
    '&a=' +
    store.state[user.store.namespace].currentUser.id +
    '&e=' +
    encodeURIComponent(state.contactEmail) +
    '&n=' +
    encodeURIComponent(state.contactName) +
    '&p=' +
    encodeURIComponent(state.contactPhone) +
    '&t=m';

  Object.keys(state.assets).forEach((group) => {
    urlData += '&m[]=' + group + ':' + state.assets[group].join(',');
  });

  return urlData;
}

function offlineUrl(state, getters) {
  const origin = window.location.origin;

  const data = b62s.compress(getters.offlineUrlRaw);
  return `${origin}/actions/mercury/outbox/redirect?r=${data}`;
}

function sendPackage({ getters, state, commit }) {
  return new Promise((resolve, reject) => {
    if (store.state[`${offline.store.namespace}`].isOnline) {
      csrfRequest().then(
        (csrfResponse) => {
          let params = {
            type: 'mediaMessages',
            'fields[recipientEmail]': state.contactEmail,
            'fields[recipientName]': state.contactName,
            'fields[recipientPhone]': state.contactPhone,
          };

          Object.keys(state.assets).forEach((group, i) => {
            params[`fields[m][${i}][product]`] = group;
            params[`fields[m][${i}][media]`] = state.assets[group].join(',');
          });

          params[csrfResponse.name] = csrfResponse.value;
          api
            .post(
              '/actions/mercury/outbox/generate',
              jsonToMultiPartFormData(params)
            )
            .then(
              (response) => {
                if (craftError(response)) {
                  queueOfflinePackage(commit, getters.offlineUrl);
                  resolve();
                } else {
                  commit('SET_LAST_SENT_URL', response.data.url);
                  resolve();
                }
              },
              () => {
                queueOfflinePackage(commit, getters.offlineUrl);
                resolve();
              }
            );
        },
        () => {
          queueOfflinePackage(commit, getters.offlineUrl);
          resolve();
        }
      );
    } else {
      queueOfflinePackage(commit, getters.offlineUrl);
      resolve();
    }
  });
}

function queueOfflinePackage(commit, offlineUrl) {
  commit('SET_LAST_SENT_URL', offlineUrl);
  store.dispatch(`${offline.queueStore.namespace}/addQueueItem`, {
    params: {
      url: offlineUrl,
    },
    action: 'pingApi',
  });
}

function hasAssets(state) {
  for (let group in state.assets) {
    if (state.assets[group].length > 0) {
      return true;
    }
  }
  return false;
}

function assetCount(state) {
  let count = 0;
  for (let group in state.assets) {
    count += state.assets[group].length;
  }
  return count;
}

function removeProduct({ commit, state }, productId) {
  let newSelection = JSON.parse(JSON.stringify(state.assets));
  delete newSelection[productId];

  commit('SET_ASSETS', newSelection);
}
